import React, { useState } from "react";
import { Button, Dropdown, Table, message } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_BUYER_PRICING,
  UPDATE_BUYER_PRICING,
} from "../../api/graphql/users";
// The same table as the prices of a tier.
import styles from "../UsersPage/ViewUser/PriceSettingsTab.module.scss";
import useIsPhone from "../../hooks/useIsPhone";
import PriceFields from "./PriceFields";

type Store = "APP_STORE" | "GOOGLE_STORE";
type Field = "DIRECT" | "KEYWORDS" | "KEYWORDS_PRO" | "RATINGS" | "REVIEWS";
type BuyerPricing = Record<Store, Record<Field, number | null>>;
// What is typed in each field while editing, so "0." stays as typed.
type Draft = Record<Store, Record<Field, string>>;

const GROUPS: {
  store: Store;
  title: string;
  fields: { field: Field; title: string }[];
}[] = [
  {
    store: "APP_STORE",
    title: "iOS",
    fields: [
      { field: "DIRECT", title: "Direct" },
      { field: "KEYWORDS", title: "Keyword" },
      // Keyword PRO is App Store only, Android has no such column
      { field: "KEYWORDS_PRO", title: "Keyword PRO" },
      { field: "RATINGS", title: "Ratings" },
      { field: "REVIEWS", title: "Reviews" },
    ],
  },
  {
    store: "GOOGLE_STORE",
    title: "Android",
    fields: [
      { field: "DIRECT", title: "Direct" },
      { field: "KEYWORDS", title: "Keyword" },
      { field: "RATINGS", title: "Ratings" },
      { field: "REVIEWS", title: "Reviews" },
    ],
  },
];

const toDraft = (pricing: BuyerPricing): Draft =>
  Object.fromEntries(
    GROUPS.map(({ store, fields }) => [
      store,
      Object.fromEntries(
        fields.map(({ field }) => [
          field,
          String(pricing[store]?.[field] ?? ""),
        ]),
      ),
    ]),
  ) as Draft;

/** The typed prices for the mutation, or null while one is not a price. */
const fromDraft = (draft: Draft): BuyerPricing | null => {
  const pricing = {} as BuyerPricing;
  for (const { store, fields } of GROUPS) {
    pricing[store] = { KEYWORDS_PRO: null } as BuyerPricing[Store];
    for (const { field } of fields) {
      const text = draft[store][field].trim();
      const value = Number(text);
      if (text === "" || !Number.isFinite(value) || value < 0) return null;
      pricing[store][field] = value;
    }
  }
  return pricing;
};

/**
 * What the contractor is paid per unit, in yuan; Campaigns 2.0 counts Buy
 * from it. Only a super admin is given it.
 */
const BuyerPricesTable: React.FC = () => {
  const { data, loading, error, refetch } = useQuery<{
    getBuyerPricing: BuyerPricing;
  }>(GET_BUYER_PRICING, { fetchPolicy: "network-only" });
  const [updateBuyerPricing, { loading: saving }] =
    useMutation(UPDATE_BUYER_PRICING);
  // The prices being edited; null when the table only shows them.
  const [draft, setDraft] = useState<Draft | null>(null);
  const isPhone = useIsPhone();

  const pricing = data?.getBuyerPricing;
  if (error) return <p>The contractor prices could not be loaded.</p>;

  const handleSave = async () => {
    const input = draft && fromDraft(draft);
    if (!input) {
      message.error("Every price must be a number of zero or more");
      return;
    }
    try {
      await updateBuyerPricing({ variables: { pricing: input } });
      await refetch();
      setDraft(null);
    } catch (e: any) {
      message.error(e?.message || "The prices were not saved");
    }
  };

  const valueOf = (store: Store, field: Field) =>
    draft ? draft[store][field] : String(pricing?.[store]?.[field] ?? "");

  if (isPhone) {
    if (!pricing) return <p>{loading ? "Loading…" : "No contractor prices"}</p>;
    return (
      <PriceFields
        groups={GROUPS.map(({ store, title, fields }) => ({
          key: store,
          title,
          fields: fields.map(({ field, title: fieldTitle }) => ({
            key: field,
            title: fieldTitle,
            value: valueOf(store, field),
            onChange: (value: string) =>
              setDraft(
                (prev) =>
                  prev && {
                    ...prev,
                    [store]: { ...prev[store], [field]: value },
                  },
              ),
          })),
        }))}
        editing={Boolean(draft)}
        saving={saving}
        onEdit={() => setDraft(toDraft(pricing))}
        onSave={handleSave}
        onCancel={() => setDraft(null)}
      />
    );
  }

  return (
    <div className={styles.wrap}>
      <Table
        bordered
        size="small"
        className={styles.table}
        tableLayout="fixed"
        pagination={false}
        loading={loading && !pricing}
        dataSource={pricing ? [pricing] : []}
        rowKey={() => "buyer-pricing"}
        columns={[
          ...GROUPS.map(({ store, title, fields }) => ({
            title,
            align: "left" as const,
            children: fields.map(({ field, title: fieldTitle }) => ({
              title: fieldTitle,
              key: `${store}_${field}`,
              render: () => (
                <input
                  type="number"
                  min={0}
                  step={0.1}
                  className={styles.input}
                  aria-label={`${title} ${fieldTitle}, yuan`}
                  value={valueOf(store, field)}
                  onChange={(e) =>
                    setDraft(
                      (prev) =>
                        prev && {
                          ...prev,
                          [store]: { ...prev[store], [field]: e.target.value },
                        },
                    )
                  }
                  disabled={!draft || saving}
                />
              ),
            })),
          })),
          {
            title: "",
            key: "action",
            width: 64, // fits the Save button in edit mode
            render: () =>
              draft ? (
                <Button
                  type="primary"
                  size="small"
                  loading={saving}
                  onClick={handleSave}
                >
                  Save
                </Button>
              ) : (
                <Dropdown
                  trigger={["click"]}
                  placement="bottomRight"
                  menu={{
                    items: [
                      {
                        key: "edit",
                        label: "Edit",
                        onClick: () => pricing && setDraft(toDraft(pricing)),
                      },
                    ],
                  }}
                >
                  <button className={styles.actionButton} aria-label="Actions">
                    <MoreOutlined />
                  </button>
                </Dropdown>
              ),
          },
        ]}
      />
    </div>
  );
};

export default BuyerPricesTable;
